/*
 * Live shipping rates in the cart and the drawer.
 *
 * Why this exists: the cart used to say "Beräknas i kassan" because the theme
 * could not know the price. It still cannot compute it — but Shopify can, and
 * it will answer over a public storefront endpoint:
 *
 *   GET /cart/shipping_rates.json?shipping_address[country]=Sweden
 *                                &shipping_address[zip]=<zip>
 *
 * That returns exactly the rates the checkout will offer for the current cart.
 * Asking beats deriving, and deriving is not even possible here: the store's
 * rates are WEIGHT-based as well as price-based, so two carts worth the same
 * money can ship at different prices. Anything hardcoded in Liquid would be a
 * guess that the checkout contradicts — which is the failure mode this whole
 * change exists to remove.
 *
 * The zip is required by the endpoint (422 "Ange ett postnummer" without it)
 * but does not change the price: Stockholm, Göteborg and Kiruna all returned
 * identical rates on 2026-08-06. So a representative zip is used for visitors
 * we know nothing about, and the customer's own default address when we do.
 * If that ever stops being true the displayed price is still a real rate, just
 * for the wrong town — so the copy stays hedged ("från", "beräknas i kassan").
 *
 * Fail-safe by construction: on any error, timeout or empty cart the markup
 * rendered by Liquid is left untouched, which is the old "Beräknas i kassan".
 * Never show a number we did not get from Shopify.
 */
(function () {
  'use strict';

  var ENDPOINT = '/cart/shipping_rates.json';
  var CACHE_PREFIX = 'hg-ship-';
  var TIMEOUT_MS = 6000;

  /* The endpoint is cheap but not free, and a quantity spinner can fire it
     several times a second. Results are keyed on the cart total so a cache hit
     is only ever reused for a cart that would price identically. */
  function cacheKey(total, zip) {
    return CACHE_PREFIX + zip + '-' + total;
  }

  function readCache(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function writeCache(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      /* Private mode, quota — the feature just loses its cache. */
    }
  }

  function targets() {
    return Array.prototype.slice.call(
      document.querySelectorAll('[data-shipping-rate]')
    );
  }

  /* Valutan och språket kommer från butiken, inte från koden: temat delas av
     flera Shopify-butiker (AGENTS.md § Flera butiker) och 'sv-SE' + ' kr' var
     hårdkodat här. Heltalskronor behålls — beloppet är en uppskattning i
     varukorgen, och ören i den raden läses som exakthet den inte har. */
  function formatMoney(amount) {
    var rounded = Math.round(amount);
    var node = document.querySelector('[data-shipping-zip]');
    var locale = (node && node.getAttribute('data-shipping-locale')) || 'sv-SE';
    var currency = (node && node.getAttribute('data-shipping-currency')) || 'SEK';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(rounded);
    } catch (err) {
      /* Okänd valutakod eller ingen Intl — hellre en naken siffra än inget. */
      return String(rounded);
    }
  }

  /* Shopify returns every rate the customer could pick. The cart should quote
     the one they will land on if they change nothing, which is the cheapest —
     the checkout preselects it. The business rate ("Företag", 98 kr) is real
     but opt-in, so quoting it here would overstate the price for everyone. */
  function cheapest(rates) {
    var best = null;
    rates.forEach(function (rate) {
      var price = parseFloat(rate.price);
      if (isNaN(price)) return;
      if (!best || price < best.price) {
        best = { price: price, name: rate.name };
      }
    });
    return best;
  }

  function render(best) {
    targets().forEach(function (node) {
      if (best.price === 0) {
        node.textContent = (window.themeStrings && window.themeStrings.freeShipping) || 'Fri frakt';
        node.setAttribute('data-shipping-free', 'true');
      } else {
        node.textContent = formatMoney(best.price);
        node.removeAttribute('data-shipping-free');
      }
    });
  }

  function fetchRates(zip, total) {
    var key = cacheKey(total, zip);
    var hit = readCache(key);
    if (hit) {
      render(hit);
      return;
    }

    /* ISO-kod, inte landsnamn. Endpointen tar BÅDA — 'SE' och 'Sweden' gav
       identiska satser (PostNord Standard 49, Företag 98) mätt 2026-08-10 —
       men landsnamnet måste i så fall vara det ENGELSKA: 'Sverige' avvisas med
       {"country":["Land/region stöds inte"]} och raden tystnar. Liquid har
       inget fält som ger det engelska namnet (`shop.address.country` ger
       "Sverige"), så koden är enda marknadsneutrala vägen. */
    var node = document.querySelector('[data-shipping-zip]');
    var country = (node && node.getAttribute('data-shipping-country')) || 'Sweden';
    var url =
      ENDPOINT +
      '?shipping_address%5Bcountry%5D=' +
      encodeURIComponent(country) +
      '&shipping_address%5Bzip%5D=' +
      encodeURIComponent(zip);

    var controller = window.AbortController ? new window.AbortController() : null;
    var timer = window.setTimeout(function () {
      if (controller) controller.abort();
    }, TIMEOUT_MS);

    window
      .fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      })
      .then(function (response) {
        /* 422 is the documented answer for an empty cart and for an address
           Shopify will not ship to. Both mean "no number to show". */
        if (!response.ok) throw new Error('rates ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var best = cheapest(data.shipping_rates || []);
        if (!best) return;
        writeCache(key, best);
        render(best);
      })
      .catch(function () {
        /* Leave the server-rendered "Beräknas i kassan" in place. */
      })
      .finally(function () {
        window.clearTimeout(timer);
      });
  }

  /* On first paint the total comes from Liquid. After a mutation cart.js hands
     us the fresh cart on the event, which is authoritative — reading the DOM
     there would race the section re-render. */
  function currentTotal(event) {
    if (event && event.detail && typeof event.detail.total_price === 'number') {
      return String(event.detail.total_price);
    }
    var node = document.querySelector('[data-cart-total-cents]');
    return node ? node.getAttribute('data-cart-total-cents') : null;
  }

  function zip() {
    var node = document.querySelector('[data-shipping-zip]');
    return (node && node.getAttribute('data-shipping-zip')) || '';
  }

  function update(event) {
    var postal = zip();
    var total = currentTotal(event);
    if (!postal || !total || total === '0') return;
    fetchRates(postal, total);
  }

  function init() {
    if (!window.fetch || !targets().length) return;
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The cart page and the drawer both re-render through the Section Rendering
     API after a mutation, which replaces the nodes we just wrote into. Both
     dispatch this event once the new markup is in the document. */
  document.addEventListener('cart:updated', update);
})();
